import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle, Loader2, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ value, hoverValue, onChange, onHover, onLeave, size = "lg" }) => {
  const sizeClasses = size === "lg" ? "h-10 w-10" : "h-6 w-6";
  
  return (
    <div className="flex gap-1" onMouseLeave={onLeave}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => onHover(star)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`${sizeClasses} transition-colors ${
              star <= (hoverValue || value)
                ? "text-yellow-400 fill-yellow-400"
                : "text-white/20"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const RatingPage = () => {
  const { bookingRef } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [driverRating, setDriverRating] = useState(0);
  const [hoverDriverRating, setHoverDriverRating] = useState(0);
  const [punctualityRating, setPunctualityRating] = useState(0);
  const [hoverPunctualityRating, setHoverPunctualityRating] = useState(0);

  useEffect(() => {
    fetchBooking();
  }, [bookingRef]);

  const fetchBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-track/status/${bookingRef}`);
      if (!response.ok) throw new Error("Booking not found");
      const data = await response.json();
      
      if (data.status !== "completed") {
        setError("This booking is not yet completed. You can rate it after the trip is finished.");
        return;
      }
      
      setBooking(data);
    } catch (err) {
      setError("Unable to find this booking. Please check your link and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select an overall rating");
      return;
    }

    setSubmitting(true);
    try {
      // We need the booking ID, so we'll fetch more details first
      const bookingResponse = await fetch(`${API_URL}/api/tracking/${bookingRef}`);
      const bookingData = await bookingResponse.json();
      
      const response = await fetch(`${API_URL}/api/ratings/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          booking_id: bookingData.id,
          rating,
          comment: comment || null,
          driver_rating: driverRating || null,
          punctuality_rating: punctualityRating || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit rating");
      }

      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (err) {
      toast.error(err.message || "Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#C8A95B]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#111114] border-yellow-500/20">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Unable to Rate</h1>
            <p className="text-white/60">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center p-4" data-testid="rating-submitted">
        <Card className="max-w-md w-full bg-[#111114] border-green-500/20">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Thank You!</h1>
            <p className="text-white/60 mb-6">
              Your feedback helps us maintain our high standards and continue improving our service.
            </p>
            <div className="flex justify-center gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-8 w-8 ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-white/20"}`}
                />
              ))}
            </div>
            <p className="text-white/30 text-sm">
              Booking Ref: {bookingRef}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white py-8 px-4" data-testid="rating-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Rate Your Experience</h1>
          <p className="text-white/60">
            How was your trip? Your feedback helps us improve.
          </p>
          <p className="text-[#C8A95B] font-mono text-sm mt-2">{bookingRef}</p>
        </div>

        <Card className="bg-[#111114] border-white/[0.06]">
          <CardContent className="p-6 space-y-8">
            {/* Overall Rating */}
            <div className="text-center">
              <p className="text-sm text-white/40 uppercase tracking-wider mb-4">Overall Experience</p>
              <StarRating
                value={rating}
                hoverValue={hoverRating}
                onChange={setRating}
                onHover={setHoverRating}
                onLeave={() => setHoverRating(0)}
              />
              <p className="text-sm text-white/60 mt-2">
                {rating === 0 ? "Tap to rate" :
                 rating === 1 ? "Poor" :
                 rating === 2 ? "Fair" :
                 rating === 3 ? "Good" :
                 rating === 4 ? "Very Good" :
                 "Excellent!"}
              </p>
            </div>

            {/* Driver Rating */}
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
              <p className="text-sm text-white/60 mb-3">Driver Performance</p>
              <StarRating
                value={driverRating}
                hoverValue={hoverDriverRating}
                onChange={setDriverRating}
                onHover={setHoverDriverRating}
                onLeave={() => setHoverDriverRating(0)}
                size="sm"
              />
            </div>

            {/* Punctuality Rating */}
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
              <p className="text-sm text-white/60 mb-3">Punctuality</p>
              <StarRating
                value={punctualityRating}
                hoverValue={hoverPunctualityRating}
                onChange={setPunctualityRating}
                onHover={setHoverPunctualityRating}
                onLeave={() => setHoverPunctualityRating(0)}
                size="sm"
              />
            </div>

            {/* Comment */}
            <div>
              <p className="text-sm text-white/40 uppercase tracking-wider mb-3">Additional Comments (Optional)</p>
              <Textarea
                placeholder="Tell us about your experience..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="bg-white/[0.03] border-white/[0.06] min-h-[100px] resize-none"
                data-testid="rating-comment"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || submitting}
              className="w-full bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-full font-semibold py-6"
              data-testid="submit-rating"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Feedback"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6">
          Limo Near Me Australia<br />
          +61 481 437 772
        </p>
      </div>
    </div>
  );
};

export default RatingPage;
